import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { useCognitoAuth } from '@/contexts/AuthContext';

export function EmailVerificationPage() {
  const { user, resendConfirmationCode, signOut, refreshUser } = useCognitoAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const hasNavigated = useRef(false);

  const state = location.state as { email?: string; invite_token?: string } | null;
  const email = state?.email ?? user?.email ?? '';
  const inviteToken = state?.invite_token;

  useEffect(() => {
    if (user?.emailVerified && !hasNavigated.current) {
      hasNavigated.current = true;
      if (inviteToken) {
        void navigate(`/invite/${inviteToken}`);
      } else {
        void navigate('/app');
      }
    }
  }, [user, navigate, inviteToken]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (refreshUser) {
        void refreshUser();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [refreshUser]);

  const handleResend = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    setError(null);

    const { error: err } = await resendConfirmationCode(email);

    setSending(false);

    if (err) {
      setError(err.message);
    } else {
      setSent(true);
      setTimeout(() => setSent(false), 5000);
    }
  }, [email, resendConfirmationCode]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    void navigate('/login');
  }, [signOut, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img src="/logo.png" alt="BeanGo" className="h-12 w-auto mx-auto mb-4" />
          </Link>
          <h1 className="text-2xl font-bold text-foreground">Verify your email</h1>
          <p className="text-muted-foreground mt-2">
            We&apos;ve sent a verification link to <strong>{email}</strong>.
          </p>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <p className="text-sm text-muted-foreground">
              Check your inbox and click the verification link to continue.
              This page will update automatically once verified.
            </p>
          </div>

          {sent && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm text-center">
              Verification email sent! Check your inbox.
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleResend} className="space-y-4">
            <button
              type="submit"
              disabled={sending || !email}
              className="w-full bg-primary text-primary-foreground py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {sending ? 'Sending...' : 'Resend verification link'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Wrong account?{' '}
            <button
              type="button"
              onClick={() => { void handleSignOut(); }}
              className="text-primary hover:underline"
            >
              Sign out
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}